import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppData } from '../context/AppDataContext';
import {
  Box, TextField, Button, Typography, Alert, Stack,
  InputAdornment, IconButton, Link, CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { authAPI } from '../services/api';

// ── App theme tokens (matching Flutter app_theme.dart) ────────────────────────
const PRIMARY   = '#174EA6';
const BG        = '#F6F8FB';
const SURFACE   = '#FFFFFF';
const TEXT_DARK = '#101828';
const TEXT_MID  = '#5B677A';
const TEXT_LIGHT= '#98A2B3';
const DIVIDER   = '#E4E7EC';

// ── Shared shell ──────────────────────────────────────────────────────────────
export function AuthShell({ children }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: BG,
        px: 2,
        py: 6,
        backgroundImage: 'linear-gradient(180deg, #FFFFFF 0%, #F6F8FB 48%, #EEF2F6 100%)',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 400 }}>
        {children}
      </Box>
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="caption" sx={{ color: TEXT_LIGHT, fontSize: '0.75rem' }}>
          © {new Date().getFullYear()} Fitistic · All rights reserved
        </Typography>
        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Link
            href="/privacy-policy"
            target="_blank"
            rel="noopener"
            underline="hover"
            sx={{ fontSize: '0.75rem', color: TEXT_MID, '&:hover': { color: 'text.primary' } }}
          >
            Privacy Policy
          </Link>
          <Typography variant="caption" sx={{ color: TEXT_LIGHT }}>·</Typography>
          <Link
            href="/terms"
            target="_blank"
            rel="noopener"
            underline="hover"
            sx={{ fontSize: '0.75rem', color: TEXT_MID, '&:hover': { color: 'text.primary' } }}
          >
            Terms &amp; Conditions
          </Link>
        </Box>
      </Box>
    </Box>
  );
}

// ── Shared brand header ───────────────────────────────────────────────────────
export function AuthBrand() {
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ mb: 3.5 }}>
      <Box
        sx={{
          width: 56, height: 56,
          borderRadius: 3,
          bgcolor: SURFACE,
          border: `1px solid ${DIVIDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
          boxShadow: '0 12px 30px rgba(16,24,40,0.08)',
        }}
      >
        <img
          src="/logo.png" alt="Fitistic"
          style={{ width: 40, height: 40, objectFit: 'contain', display: 'block' }}
          onError={e => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <Box
          sx={{
            display: 'none', width: '100%', height: '100%',
            alignItems: 'center', justifyContent: 'center',
            bgcolor: '#F05A28', borderRadius: 'inherit',
          }}
        >
          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 22 }}>F</Typography>
        </Box>
      </Box>
      <Typography fontWeight={700} sx={{ fontSize: '1.125rem', color: TEXT_DARK, letterSpacing: '-0.01em' }}>
        Fitistic
      </Typography>
    </Stack>
  );
}

// ── Shared card ───────────────────────────────────────────────────────────────
export function AuthCard({ title, subtitle, children }) {
  return (
    <>
      <Typography
        variant="h5"
        fontWeight={700}
        textAlign="center"
        sx={{ mb: 2.5, color: TEXT_DARK, fontSize: '1.25rem', letterSpacing: '-0.02em' }}
      >
        {title}
      </Typography>

      <Box
        sx={{
          bgcolor: SURFACE,
          border: `1px solid ${DIVIDER}`,
          borderRadius: 2,
          p: { xs: 2.5, sm: 3 },
          boxShadow: '0 18px 48px rgba(16,24,40,0.10), 0 1px 2px rgba(16,24,40,0.04)',
        }}
      >
        {subtitle && (
          <Typography
            variant="body2"
            sx={{ mb: 2.5, color: TEXT_MID, fontSize: '0.8125rem', lineHeight: 1.6 }}
          >
            {subtitle}
          </Typography>
        )}
        {children}
      </Box>
    </>
  );
}

// ── Field label row ───────────────────────────────────────────────────────────
function FieldLabel({ children, action }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
      <Typography
        variant="body2" fontWeight={600}
        sx={{ fontSize: '0.8125rem', color: TEXT_DARK }}
      >
        {children}
      </Typography>
      {action}
    </Stack>
  );
}

// ── Shared text field style ───────────────────────────────────────────────────
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    bgcolor: SURFACE,
    fontSize: '0.875rem',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: DIVIDER },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: PRIMARY,
      borderWidth: '2px',
    },
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
      WebkitBoxShadow: `0 0 0 1000px ${SURFACE} inset`,
      WebkitTextFillColor: TEXT_DARK,
      caretColor: TEXT_DARK,
      transition: 'background-color 9999s ease-out 0s',
    },
    '& input:-moz-autofill': {
      boxShadow: `0 0 0 1000px ${SURFACE} inset`,
      WebkitTextFillColor: TEXT_DARK,
      caretColor: TEXT_DARK,
    },
  },
};

// ── Login page ────────────────────────────────────────────────────────────────
export default function Login() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const { reloadGyms, reloadPremium } = useAppData();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await authAPI.login({ email, password });
      const { token, user } = response.data.data;
      if (!['admin', 'trainer', 'superadmin', 'gym_admin', 'sales_staff', 'coach', 'front_desk', 'manager'].includes(user.role)) {
        setError('Access denied. Admin or staff role required.');
        return;
      }
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      await reloadGyms();
      await reloadPremium();
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Incorrect email or password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell>
      <AuthBrand />

      <AuthCard title="Sign in to Fitistic">
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2.5, py: 0.75, borderRadius: 2,
              fontSize: '0.8125rem',
              bgcolor: 'rgba(255,82,82,0.06)',
              border: '1px solid rgba(255,82,82,0.2)',
              color: '#FF5252',
              '& .MuiAlert-icon': { color: '#FF5252', fontSize: 16 },
            }}
          >
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {/* Email */}
            <Box>
              <FieldLabel>Email address</FieldLabel>
              <TextField
                required fullWidth autoFocus autoComplete="email"
                type="email" size="small"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                sx={fieldSx}
              />
            </Box>

            {/* Password */}
            <Box>
              <FieldLabel
                action={
                  <Link
                    component="button" type="button" underline="none"
                    onClick={() => navigate('/forgot-password')}
                    sx={{
                      fontSize: '0.75rem', color: PRIMARY, fontWeight: 600,
                      '&:hover': { color: '#5850E6' },
                    }}
                  >
                    Forgot password?
                  </Link>
                }
              >
                Password
              </FieldLabel>
              <TextField
                required fullWidth autoComplete="current-password"
                type={showPass ? 'text' : 'password'}
                size="small"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Your password"
                sx={fieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small" edge="end" tabIndex={-1}
                        onClick={() => setShowPass(v => !v)}
                        sx={{ color: TEXT_LIGHT, '&:hover': { color: TEXT_MID } }}
                      >
                        {showPass
                          ? <VisibilityOff sx={{ fontSize: 17 }} />
                          : <Visibility sx={{ fontSize: 17 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Submit */}
            <Button
              type="submit" fullWidth variant="contained"
              disabled={loading}
              sx={{
                mt: 0.5, py: 1.25,
                fontWeight: 700, fontSize: '0.9375rem',
                borderRadius: '12px',
                bgcolor: PRIMARY,
                boxShadow: `0 4px 16px rgba(108,99,255,0.35)`,
                '&:hover': {
                  bgcolor: '#5850E6',
                  boxShadow: `0 6px 20px rgba(108,99,255,0.45)`,
                  transform: 'translateY(-1px)',
                },
                '&:active': { transform: 'translateY(0)' },
                transition: 'all 0.2s',
              }}
            >
              {loading
                ? <CircularProgress size={18} sx={{ color: '#fff' }} />
                : 'Sign in'}
            </Button>
          </Stack>
        </Box>
      </AuthCard>

      {/* Role hint */}
      <Box
        sx={{
          mt: 2, p: 1.75,
          bgcolor: SURFACE,
          border: `1px solid ${DIVIDER}`,
          borderRadius: 3,
          textAlign: 'center',
        }}
      >
        <Typography variant="caption" sx={{ color: TEXT_MID, fontSize: '0.75rem' }}>
          Access is restricted to{' '}
          <Typography component="span" variant="caption" fontWeight={700} sx={{ color: TEXT_DARK, fontSize: '0.75rem' }}>
            admins and trainers
          </Typography>
          {' '}only.
        </Typography>
      </Box>
    </AuthShell>
  );
}
